import { ArrowBackIosNew, ArrowForwardIos } from '@mui/icons-material';
import { IconButton, styled, useTheme } from '@mui/material';
import { type RefObject, useEffect, useRef, useState } from 'react';
import { Divider } from '#styled.ts';

const Container = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'flex-end',
}));

export type CarouselControlsProps = {
  containerRef?: RefObject<HTMLDivElement | null>;
  hasNextPage?: boolean;
  scrollRef?: RefObject<HTMLDivElement | null>;
};

export const CarouselControls = ({ containerRef, hasNextPage, scrollRef }: CarouselControlsProps) => {
  const theme = useTheme();
  const [prevDisabled, setPrevDisabled] = useState<boolean>(true);
  const [nextDisabled, setNextDisabled] = useState<boolean>(true);
  const controlsDisabledRef = useRef<{ next: boolean; prev: boolean }>({ next: true, prev: true });

  useEffect(() => {
    const containerElement = containerRef?.current;
    const scrollElement = scrollRef?.current;

    const onScroll = () => {
      if (!containerElement || !scrollElement) {
        return;
      }

      if (containerElement.scrollLeft > 0 && controlsDisabledRef.current.prev) {
        controlsDisabledRef.current.prev = false;
        setPrevDisabled(controlsDisabledRef.current.prev);
      } else if (containerElement.scrollLeft === 0 && !controlsDisabledRef.current.prev) {
        controlsDisabledRef.current.prev = true;
        setPrevDisabled(controlsDisabledRef.current.prev);
      }

      if (
        containerElement.scrollLeft + containerElement.offsetWidth < scrollElement.offsetWidth &&
        controlsDisabledRef.current.next
      ) {
        controlsDisabledRef.current.next = false;
        setNextDisabled(controlsDisabledRef.current.next);
      } else if (
        containerElement.scrollLeft + containerElement.offsetWidth >= scrollElement.offsetWidth &&
        !controlsDisabledRef.current.next
      ) {
        controlsDisabledRef.current.next = true;
        setNextDisabled(controlsDisabledRef.current.next);
      }
    };

    if (containerElement && scrollElement) {
      const { offsetWidth: containerOffsetWidth } = containerElement;
      const { offsetWidth: scrollOffsetWidth } = scrollElement;

      if (containerOffsetWidth >= scrollOffsetWidth) {
        return;
      }

      controlsDisabledRef.current.next = false;
      setNextDisabled(controlsDisabledRef.current.next);
      containerElement.addEventListener('scroll', onScroll, true);
    }

    return () => {
      containerElement?.removeEventListener('scroll', onScroll);
    };

    // We want to know when the ref value changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRef?.current, scrollRef?.current, hasNextPage]);

  return (
    <>
      <Divider sx={{ marginBottom: theme.spacing(1), marginTop: theme.spacing(2) }} />
      <Container>
        <IconButton
          aria-label="Go backward"
          disabled={prevDisabled}
          onClick={() => {
            containerRef?.current?.scrollTo({
              behavior: 'smooth',
              left: containerRef.current.scrollLeft - containerRef.current.offsetWidth,
            });
          }}
          sx={{
            color: theme.supportingTextPrimary,
          }}
        >
          <ArrowBackIosNew
            sx={{
              [theme.breakpoints.down('sm')]: {
                fontSize: '2rem',
              },
              fontSize: '2.5rem',
            }}
          />
        </IconButton>
        <IconButton
          aria-label="Go forward"
          disabled={nextDisabled}
          onClick={() => {
            containerRef?.current?.scrollTo({
              behavior: 'smooth',
              left: containerRef.current.scrollLeft + containerRef.current.offsetWidth,
            });
          }}
          sx={{
            color: theme.supportingTextPrimary,
          }}
        >
          <ArrowForwardIos
            sx={{
              [theme.breakpoints.down('sm')]: {
                fontSize: '2rem',
              },
              fontSize: '2.5rem',
            }}
          />
        </IconButton>
      </Container>
    </>
  );
};
